<script setup lang="ts">
import { ref, computed, type PropType, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { useMutation } from '@tanstack/vue-query';
import { type PatientFilter } from '@/api/patients';
import { type CaregiverFilter } from '@/api/caregivers';
import { shiftsApi, type ShiftBatchUpdateForm } from '@/api/shifts';
import type { AxiosError } from 'axios';
import ShiftConflictDetector from '@/components/shifts/shift-conflict-detector.vue';
import { useTableFilter } from '@/composables/useTableFilter';

const nameFilter = useTableFilter(['fullName']);

const { t } = useI18n();

const emit = defineEmits(['close', 'refetch', 'success']);

const props = defineProps({
  patient: {
    type: Object as PropType<PatientFilter>,
    required: true,
  },
  caregiver: {
    type: Object as PropType<CaregiverFilter>,
    required: true,
  },
  caregivers: {
    type: Array as PropType<CaregiverFilter[]>,
    default: () => [],
  },
});

function futureDateValidator() {
  return yup.string().required(t('shiftBatchUpdateForm.fieldRequired')).test(
    'future-date',
    t('shiftBatchUpdateForm.dateFuture'),
    (value) => {
      if (!value) {
        return true;
      }
      const selectedDate = new Date(value.replace(/-/g, '/'));
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      return selectedDate > today;
    },
  );
}

const validationSchema = yup.object({
  newCaregiverId: yup.number().required(t('shiftBatchUpdateForm.fieldRequired')),
  startDate: futureDateValidator(),
  endDate: futureDateValidator(),
});

const initialValues = {
  patientId: props.patient.id,
  oldCaregiverId: props.caregiver.id,
  newCaregiverId: undefined as number | undefined,
  startDate: '',
  endDate: '',
};

const { handleSubmit, values, errors, defineField } = useForm({ validationSchema, initialValues });

const [newCaregiverId, newCaregiverIdAttrs] = defineField('newCaregiverId');
const [startDate, startDateAttrs] = defineField('startDate');
const [endDate, endDateAttrs] = defineField('endDate');

const { mutate, isError, error, isPending, isSuccess } = useMutation<unknown, AxiosError>({
  mutationFn: () => shiftsApi.batchUpdate({ ...values } as ShiftBatchUpdateForm),
  onSuccess: () => {
    emit('success');
    emit('refetch');
  },
});

const submitButtonDisabled = ref(false);
const conflictDetector = ref<InstanceType<typeof ShiftConflictDetector>>();

function handleConflictCancel() {
  submitButtonDisabled.value = false;
}

const onSubmit = handleSubmit(() => {
  submitButtonDisabled.value = true;
  conflictDetector.value?.checkConflicts({ ...values } as ShiftBatchUpdateForm);
});

watch(isSuccess, async () => {
  if (isSuccess.value === true) {
    const waitMs = 1500;
    await new Promise((resolve) => setTimeout(resolve, waitMs));
    emit('close');
  }
});

const availableCaregivers = computed(() =>
  props.caregivers?.filter(caregiver => caregiver.id !== props.caregiver.id) || [],
);

const tomorrowDate = computed(() => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  return tomorrow.toISOString().split('T')[0];
});
</script>

<template>
  <v-card
    class="w-100 pa-4"
    flat
  >
    <v-card-title>
      {{ t('shiftBatchUpdateForm.title') }} de {{ caregiver.fullName }}
    </v-card-title>
    <v-form
      v-if="!isPending && !isSuccess"
      class="w-100 ma-0"
      @submit.prevent="onSubmit"
    >
      <v-card flat>
        <v-card-text>
          {{ t('shiftBatchUpdateForm.explanation') }}
        </v-card-text>

        <v-row class="mx-1 d-flex">
          <v-col cols="12">
            <v-autocomplete
              v-model="newCaregiverId"
              :items="availableCaregivers"
              item-title="fullName"
              item-value="id"
              variant="outlined"
              class="mb-2"
              label="Nuevo Profesional"
              placeholder="Selecciona un profesional"
              clearable
              :disabled="!availableCaregivers.length"
              no-data-text="No hay otros profesionales disponibles"
              v-bind="newCaregiverIdAttrs"
              :error-messages="errors.newCaregiverId"
              :custom-filter="nameFilter"
            />
          </v-col>
        </v-row>

        <div
          class="mb-4 mt-n4 d-flex"
        >
          <v-col>
            <v-text-field
              v-model="startDate"
              type="date"
              variant="outlined"
              v-bind="startDateAttrs"
              label="Fecha de Inicio"
              :error-messages="errors.startDate"
              :min="tomorrowDate"
            />
          </v-col>
          <v-col>
            <v-text-field
              v-model="endDate"
              type="date"
              variant="outlined"
              v-bind="endDateAttrs"
              label="Fecha de Fin"
              :error-messages="errors.endDate"
              :min="tomorrowDate"
            />
          </v-col>
        </div>

        <v-card-actions class="pa-4">
          <v-btn
            @click="emit('close')"
          >
            Cerrar
          </v-btn>
          <v-spacer />
          <v-btn
            class="mb-2 text-white border bg-primary"
            :loading="isPending"
            :disabled="isPending"
            @click="onSubmit"
          >
            Guardar Cambios
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
    <v-col
      v-if="isPending"
      class="pb-20"
    >
      <v-row class="justify-center d-flex">
        <v-progress-circular
          class="mx-auto"
          indeterminate
          color="primary"
        />
      </v-row>
      <v-row class="justify-center mt-8 d-flex">
        <p>Estamos actualizando los turnos, esto puede tomar unos segundos...</p>
      </v-row>
    </v-col>
    <v-col v-if="isSuccess">
      <v-row class="justify-center d-flex">
        <v-icon
          icon="mdi-check-circle"
          color="primary"
          size="50"
        />
      </v-row>
      <v-row class="justify-center mt-8 d-flex">
        <p>Turnos actualizados exitosamente!</p>
      </v-row>
    </v-col>
    <v-alert
      v-if="isError && error"
      class="mt-4 rounded"
      :type="'error'"
    >
      {{ error?.response?.status === 404 ? t('shiftBatchUpdateForm.notFoundError') : t('userSession.defaultError') }}
    </v-alert>

    <shift-conflict-detector
      ref="conflictDetector"
      mode="batch-update"
      @confirm="mutate"
      @cancel="handleConflictCancel"
    />
  </v-card>
</template>
